import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  AddVibeAppDomainBodySchema,
  AddVibeAppDomainPathVarsSchema,
  AddVibeAppDomainResponseSchema,
  DeleteVibeAppDomainPathVarsSchema,
  DeleteVibeAppDomainResponseSchema,
  GetVibeOrgAppDefaultsResponseSchema,
  ListVibeAppDomainsPathVarsSchema,
  ListVibeAppDomainsResponseSchema,
  SetVibeAppPrimaryDomainBodySchema,
  SetVibeAppPrimaryDomainPathVarsSchema,
  SetVibeAppPrimaryDomainResponseSchema,
  SetVibeOrgAppDefaultsBodySchema,
  SetVibeOrgAppDefaultsResponseSchema,
  VerifyVibeAppDomainPathVarsSchema,
  VerifyVibeAppDomainResponseSchema
} from "../schemas";

export const VibeDomainEndpoints = {
  // ---------------------------------------------------------------------------
  // Custom domains — customer-owned hosts an app is served on beside its
  // platform host. Every route 404s for an app outside the caller's org.
  // ---------------------------------------------------------------------------

  /** The app's custom domains, each with the DNS records it needs. */
  ListAppDomains: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/domains",
    PathVars: ListVibeAppDomainsPathVarsSchema,
    Response: ApiSuccessSchema(ListVibeAppDomainsResponseSchema)
  },

  /**
   * Attach a host to an app. The host is canonicalised (lowercase, punycode, no
   * root dot) and its kind — APEX or SUBDOMAIN — is derived from the Public
   * Suffix List. 400 for a host that cannot be attached (an IP, a wildcard, a
   * public suffix, anything under a platform domain); 409 when any app, in any
   * organization, already holds it. The response carries the DNS records to
   * create.
   */
  AddAppDomain: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/domains",
    PathVars: AddVibeAppDomainPathVarsSchema,
    Body: AddVibeAppDomainBodySchema,
    Response: ApiSuccessSchema(AddVibeAppDomainResponseSchema)
  },

  /** Detach a host. Frees it for any other app to claim. */
  DeleteAppDomain: {
    method: "DELETE" as const,
    path: "/api/vibe/apps/:appId/domains/:host",
    PathVars: DeleteVibeAppDomainPathVarsSchema,
    Response: ApiSuccessSchema(DeleteVibeAppDomainResponseSchema)
  },

  /**
   * Check the host's DNS now instead of waiting for the next sweep. A domain
   * whose DNS points at the edge moves from PENDING_DNS (or FAILED) to
   * ISSUING_CERT; one that does not stays where it is, with `statusReason`
   * saying what was found.
   */
  VerifyAppDomain: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/domains/:host/verify",
    PathVars: VerifyVibeAppDomainPathVarsSchema,
    Response: ApiSuccessSchema(VerifyVibeAppDomainResponseSchema)
  },

  /**
   * Make one of THIS app's ACTIVE custom domains its primary host — named by
   * `domainId` or by `host` — or clear it with `{ domainId: null }`. A host is
   * canonicalised server-side: 400 when it cannot be a host, 404 when it is not
   * a domain of this app. 409 when the domain is not an ACTIVE domain of this app.
   */
  SetAppPrimaryDomain: {
    method: "PUT" as const,
    path: "/api/vibe/apps/:appId/primary-domain",
    PathVars: SetVibeAppPrimaryDomainPathVarsSchema,
    Body: SetVibeAppPrimaryDomainBodySchema,
    Response: ApiSuccessSchema(SetVibeAppPrimaryDomainResponseSchema)
  },

  /**
   * Org-scoped read of the defaults a NEW app inherits at creation. An
   * organization that has never configured anything reads back `OFF` with a
   * null `updatedAt` — no row is written to answer this.
   */
  GetOrgAppDefaults: {
    method: "GET" as const,
    path: "/api/vibe/org-app-defaults",
    Response: ApiSuccessSchema(GetVibeOrgAppDefaultsResponseSchema)
  },

  /**
   * Set the defaults new apps inherit. Affects apps created AFTER the write and
   * nothing else — see `VibeOrgAppDefaultsSchema` for why the org value is a
   * default rather than a floor, and why an existing app is never re-read.
   */
  SetOrgAppDefaults: {
    method: "PUT" as const,
    path: "/api/vibe/org-app-defaults",
    Body: SetVibeOrgAppDefaultsBodySchema,
    Response: ApiSuccessSchema(SetVibeOrgAppDefaultsResponseSchema)
  }
} as const;
